import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CONTENT_CARD, PAGE, SECTION_COLUMN, CONTAINER, GROUP, CAROUSEL_CARD, MENU, FORM_STEP, CAROUSEL } from 'constants/widgetTypes';

import { toggleEditMode, updateWidgetProperty, updateWidgetContent, updateWidgetActions, updateBulkWidgetProperty } from 'actions/editor';
import { associateFontsWithWigdet } from 'actions/fonts';
import { calculateBoundsForElement, getParentContainerOfType, isWidgetHasHotspotChild, getWidgetType, isTopParentIsHotspot } from 'components/Editor/helper/widget';
import { useHotspotContainerSelector, useAllowCreateLiveElement, useSelectedWidgetSelector } from 'components/Editor/hooks/useEditorSession';
import { getBoxShadow } from 'services/cssHelper.service';

import _ from 'lodash';
import cx from 'classnames';
import { Tabs, Tab } from 'react-bootstrap';

import { EditorContext } from '../../index';
import DropShadow from 'components/Editor/components/properties/dropShadow';
import Tooltip from 'components/Tooltip';
import { VariableProperty } from 'components/Editor/components/variables';
import { Property } from '../../components/properties/';
import { Animation, AnimationProperty } from 'components/Editor/components/animations';
import Actions from '../../components/actions';
import { Fonts } from '@paperflite-marketing/cleverstory-commons';
import { Font } from '../../components/properties/fonts';
import { LiveElementCreationProperty } from '../../components/liveElements';
import { HotspotActionButton, HotspotCreationProperty } from '../../components/hotspot';

import { getColorsConfig } from 'services/cssHelper.service';

import { updateBrandkitButtons } from 'actions/brandKit/buttons';
import { createMixpanelEvent } from 'actions/mixpanel';
import { MIXPANEL_BRANDKIT_CREATED } from 'constants/mixpanelEvents';
import { BrandButtonProperty } from 'components/Editor/components/brandElements';
import userRoleService from 'services/userRoles.service';

import styles from './button.module.scss';

const ButtonWidgetComponent = function ButtonEditor({ className, style, widgetProps, widgetRef, children }) {
  const ref = widgetRef;
  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const [state, setState] = useState({
    active: 'regular',
    buttonStyles: {}
  });

  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');
  const content = _.get(widgetProps, 'content');
  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));

  const properties = _.get(widgetProps, 'properties', {});
  const deviceProperties = _.get(properties, `${breakpoint}`, {});

  Fonts.useFont(_.get(widgetProps, 'fonts') || []);

  useEffect(() => {
    var buttonStyles = getStyles(properties, breakpoint, 'regular');
    setState(state => {
      return {
        ...state,
        buttonStyles: {
          ...buttonStyles, 
          width: style['width'],
          height: style['height']
        }
      }
    })
  }, [state.active, deviceProperties])

  const handleOnMouseOver = () => {
    setState(state => {
      return {
        ...state,
        active: 'hover'
      }
    })
  }

  const handleOnMouseOut = () => {
    setState(state => {
      return {
        ...state,
        active: 'regular'
      }
    })
  }


  return (
    <div id={id}
      ref={ref}
      className={cx(className, styles.button)}
      style={style}
      data-widget-id={id}
      data-widget-type={type}
      data-selectable={true}
      onMouseOver={handleOnMouseOver}
      onMouseOut={handleOnMouseOut}>
      <Animation widget={widgetProps}>
        <div className={styles.inner} style={state.buttonStyles}>
          {content}
        </div>
      </Animation>
    </div>
  )
}

const buttonWidgetEquals = function (prevProps, nextProps) {
  const paths = ['className', 'style', 'widgetProps', 'widgetRef'];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

const ButtonWidget = React.memo(ButtonWidgetComponent, buttonWidgetEquals);

const ButtonWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();

  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);

  const isHotspotContainer = useHotspotContainerSelector(editorSession, props);
  const allowLiveElement = useAllowCreateLiveElement(editorSession, props);

  const id = _.get(props, 'uid');
  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const selectedWidgets = useSelectedWidgetSelector(editorSession);

  const batchEdit = useSelector((state) => {
    return _.get(state, `${editorSession}.present.batchEdit`);
  });
  
  useEffect(() => {
    if (selectedWidgets?.length === 0 && batchEdit) {
      dispatch(toggleEditMode(editorSession, id, false, null, false));
    }
  },[batchEdit, selectedWidgets]);

  useEffect(() => {
    setView(null);
  },[id])

  const allowVariable = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`)) === 'PAGE';
  const allowBrandkit = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`)) !== 'PALETTE_COMPONENT';

  const template = _.get(props, 'template.widget') != null;

  const actions = _.get(props, 'actions.0');

  const handleEditMode = () => {
    dispatch(toggleEditMode(editorSession, props.uid, true));
    setView(null);
  }

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false, view));
    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  const handleActionChange = (action) => {
    const actions = action ? [action] : [];
    dispatch(updateWidgetActions(editorSession, props.uid, actions));
  }

  const handleLiveElement = () => {
    setView('liveElements')
  }

  const handleVariable = () => {
    setView('variables')
  }

  useEffect(() => {
    const assetExists = _.get(props, 'actions.0.value') != null;

    if(!assetExists) {
      const actions = [];
      dispatch(updateWidgetActions(editorSession, props.uid, actions));
    }

  }, [actions]);

  const handleBrandMedia = (name, paletteId) => {
    const componentName = name;
    const { 
      breakpoint, 
      id, 
      parent, 
      order, 
      systemDefined, 
      actions, 
      parameter, 
      editorSession, 
      ...cleanedWidget 
    } = props;
    
    const request = [
        {
          name: componentName,
          order: 1,
          widgets: [{
            ...cleanedWidget,
            properties: {
              ...cleanedWidget.properties,
              desktop: {
                ...cleanedWidget.properties.desktop,
                layout: {
                  ...cleanedWidget.properties.desktop.layout,
                  transform: {
                    translate: "0px, 0px",
                    rotate: "0deg"
                  }
                },
              },
              mobile: {
                ...cleanedWidget.properties.mobile,
                layout: {
                  ...cleanedWidget.properties.mobile.layout,
                  transform: {
                    translate: "0px, 0px",
                    rotate: "0deg"
                  }
                },
              },
            }
          }]
        }
      ];

    dispatch(updateBrandkitButtons(paletteId, request));

    var mixpanelPayload = {
      brandkit_element_type: "Buttons"
    }

    dispatch(createMixpanelEvent(MIXPANEL_BRANDKIT_CREATED, mixpanelPayload));
  };

  const fields = [
    { name: 'Text', path: 'content', type:"TEXT" },
    { name: 'Text Color', path: 'properties.desktop.regular.font.color', type:"COLOR" },
    { name: 'Button Color', path: 'properties.desktop.regular.background', type:"COLOR" },
    { name: 'Hover Color', path: 'properties.desktop.hover.background', type:"COLOR" },
    { name: 'Hover Text Color', path: 'properties.desktop.hover.font.color', type:"COLOR" }
  ];

  return (
    <>
      <Property.Action className={styles.textButton} onClick={handleEditMode.bind(this, 'text')}>Format text</Property.Action>
      <Property.Divider />
      <Property.Action className={styles.iconButton} tooltip="Insert Link" onClick={() => changeView('action')}>
        <i className={cx("cleverstory-link")} />
      </Property.Action>
      <Property.Action className={styles.iconButton} tooltip="Customize" onClick={() => changeView('style')}>
        <i className={cx("cleverstory-customize")} />
      </Property.Action>
      <Property.Action className={styles.iconButton} tooltip="Animation" onClick={() => changeView('animation')}>
        <i className={cx("cleverstory-animations")} />
      </Property.Action>
      {!template && allowLiveElement &&  
        <Property.Action className={styles.iconButton} tooltip='Live Element' onClick={handleLiveElement}>
          <i className={'cleverstory-live-element'} />
        </Property.Action>
      }

      {!template && !isHotspotContainer && 
        <HotspotActionButton className={styles.iconButton} onClick={() => changeView('hotspot')} widget={props} />
      }

      {allowVariable && 
        <Property.Action className={styles.iconButton} tooltip='Make Variable' onClick={handleVariable}>
          <i className={'cleverstory-variable'} data-track-event="Mixpanel" data-track-props="Make Variable" data-widget-type="Buttons"/>
        </Property.Action>
      }

      {!template && brandkitEnabled && allowBrandkit && userRoleService.canManageUsers() &&
        <Property.Action className={styles.iconButton} tooltip='Add to Brand Kit' onClick={() => changeView('brandMedia')}>
          <i className={'cleverstory-brandmedia'} />
        </Property.Action>
      }
      
      <Property.Action className={styles.iconButton} tooltip="Help" onClick={() => { window.open(`${process.env.REACT_APP_CLEVERSTORY_SUPPORT}`, '_blank') }}>
        <i className={'cleverstory-help'} />
      </Property.Action>
      
      {editMode &&
        <ButtonTextEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'action' &&
        <Actions
          target={id}
          editorSession={editorSession}
          value={_.get(props, 'actions.0')}
          onChange={handleActionChange}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'animation' &&
        <AnimationProperty
          ids={[id]}
          editor={editorSession}
          editorContext={editorContext}
          widgets={[props]}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'style' &&
        <ButtonStyleEditor
          editor={editorSession}
          widgets={[props]}
          onCancel={() => {
            closePopup()
          }}
        />
      }
      
      {view === 'liveElements' &&
        <LiveElementCreationProperty elements={[props]} onCancel={() => closePopup()} />
      }

      {view === 'variables' &&
        <VariableProperty widget={props} onCancel={() => closePopup()} fields={fields} onConfirm={()=>{console.log("uploaded variable")}} />
      }

      {view === 'hotspot' &&
        <HotspotCreationProperty widget={props} onCancel={() => closePopup()} />
      }
      {view === 'brandMedia' &&
        <BrandButtonProperty type={'BUTTON'} widget={props} onCancel={() => closePopup()} handleBrandMedia={handleBrandMedia} />
      }
    </>
  );
}

const ButtonWidgetMobileAction = (props) => {
  const dispatch = useDispatch();

  const { editorSession, uid, ...rest } = props;

  const isHotspotContainer = useHotspotContainerSelector(editorSession, props);
  const allowLiveElement = useAllowCreateLiveElement(editorSession, props);

  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === uid
  });
  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);

  const allowBrandkit = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`)) !== 'PALETTE_COMPONENT';

  const template = _.get(props, 'template.widget') != null;

  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const properties = useSelector(state => _.get(rest, `properties.mobile`));
  const fontSize = _.get(properties, 'regular.font.fontSize') || 14;

  const handleEditMode = () => {
    dispatch(toggleEditMode(editorSession, uid, true));
  }

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, uid, false));
    setView(view);
  }

  const closePopup = () => {
    setView(null);
    dispatch(toggleEditMode(editorSession, uid, false));
  }

  const increaseFontSize = () => {
    const updated = parseInt(fontSize);
    var value = Math.min(updated + 1, 150);
    handleSizeChange(value);
  }

  const decreaseFontSize = () => {
    const updated = parseInt(fontSize);
    var value = Math.max(updated - 1, 6);
    handleSizeChange(value);
  }

  const handleSizeChange = (value) => {
    const buttonStyle = _.get(properties, 'regular', {});
    var request = {
      ...buttonStyle,
      font: { ...buttonStyle.font }
    }

    _.set(request, 'font.fontSize', value);

    dispatch(updateWidgetProperty(editorSession, uid, 'mobile', {
      regular: request
    }));
  }

  const handleBrandMedia = (name, paletteId) => {
    const componentName = name;
    const { 
      breakpoint, 
      id, 
      parent, 
      order, 
      systemDefined, 
      actions, 
      parameter, 
      editorSession, 
      ...cleanedWidget 
    } = props;
    
    const request = [
        {
          name: componentName,
          order: 1,
          widgets: [{
            ...cleanedWidget,
            properties: {
              ...cleanedWidget.properties,
              desktop: {
                ...cleanedWidget.properties.desktop,
                layout: {
                  ...cleanedWidget.properties.desktop.layout,
                  transform: {
                    translate: "0px, 0px",
                    rotate: "0deg"
                  }
                },
              },
              mobile: {
                ...cleanedWidget.properties.mobile,
                layout: {
                  ...cleanedWidget.properties.mobile.layout,
                  transform: {
                    translate: "0px, 0px",
                    rotate: "0deg"
                  }
                },
              },
            }
          }]
        }
      ];
      
    dispatch(updateBrandkitButtons(paletteId, request));

    var mixpanelPayload = {
      brandkit_element_type: "Buttons"
    }

    dispatch(createMixpanelEvent(MIXPANEL_BRANDKIT_CREATED, mixpanelPayload));
  };

  const widgetProps = {
    ...rest,
    uid: uid
  }

  return (
    <>
      <Property.Action className={styles.iconButton} tooltip='Increase Size' onClick={increaseFontSize}>
        <i className={cx("cleverstory-text-maximize")} />
      </Property.Action>
      <Property.Action className={styles.iconButton} tooltip='Decrease Size' onClick={decreaseFontSize}>
        <i className={cx("cleverstory-text-minimize")} />
      </Property.Action>
      <Property.Divider />
      <Property.Action className={styles.iconButton} tooltip='Customize' onClick={handleEditMode}>
        <i className={cx("cleverstory-customize", styles.settingsIcon)} />
      </Property.Action>
      {props?.showAnimation !== false &&
        <Property.Action className={styles.iconButton} tooltip='Animation' onClick={() => changeView('animation')}>
          <i className={cx("cleverstory-animations")} />
        </Property.Action>
      }
      {!template && allowLiveElement && 
        <Property.Action className={styles.iconButton} tooltip='Live Element' onClick={() => changeView('liveElements')}>
          <i className={'cleverstory-live-element'} />
        </Property.Action>
      }
      {!template && !isHotspotContainer && 
        <HotspotActionButton onClick={() => changeView('hotspot')} widget={props} />
      }
      {!template  && brandkitEnabled  && allowBrandkit  && userRoleService.canManageUsers() &&
        <Property.Action className={styles.iconButton} tooltip='Add to Brand Kit' onClick={() => changeView('brandMedia')}>
          <i className={'cleverstory-brandmedia'} />
        </Property.Action>
      }

      {editMode &&
        <ButtonMobileStyleEditor
          editor={editorSession}
          widgets={[widgetProps]}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'animation' &&
        <AnimationProperty
          ids={[uid]}
          editor={editorSession}
          editorContext={editorContext}
          widgets={[widgetProps]}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'liveElements' &&
        <LiveElementCreationProperty elements={[props]} onCancel={() => closePopup()} />
      }

      {view === 'hotspot' &&
        <HotspotCreationProperty widget={props} onCancel={() => closePopup()} />
      }

      {view === 'brandMedia' &&
        <BrandButtonProperty type={'BUTTON'} widget={props} onCancel={() => closePopup()} handleBrandMedia={handleBrandMedia} />
      }
    </>
  );
}

ButtonWidget.sizeConfig = (widget) => {
  const text = _.get(widget, 'content');

  const estimatedSizeBytes = (text?.length || 1) * 1.5;

  // Convert bytes to kilobytes and megabytes
  const sizeKB = estimatedSizeBytes / 1024;
  const sizeMB = sizeKB / 1024;

  return {
    size: sizeMB,
    type: 'button'
  };
}

ButtonWidget.moveableConfig = (editorSession, widget) => {
  const contentCard = getParentContainerOfType(editorSession, widget.uid, CONTENT_CARD);
  var bounds = null;
  if (contentCard) {
    bounds = calculateBoundsForElement(contentCard);
  }
  
  var id = widget && widget.uid ? widget.uid : null;
  const group = getParentContainerOfType(editorSession, id, GROUP);

  return {
    draggable: true,
    resizable: true,
    rotatable: true,
    selectable: true,
    cloneable: true,
    deletable: true,
    bounds: bounds,
    renderDirections: ['e', 'w', 's'],
    onReparent: (widget, parent) => {
      let hasTopParentIsHotspot = false;
      const parentWidget = getParentContainerOfType(editorSession, widget, CONTENT_CARD);
      
      const hasHotspotChild = isWidgetHasHotspotChild(editorSession, widget);
      const parentType = getWidgetType(editorSession, parent);
     
      if (parentType === CAROUSEL_CARD){
        hasTopParentIsHotspot = isTopParentIsHotspot(editorSession, parent, CAROUSEL);
      }

      const denyHostpotReparent = hasHotspotChild && ![PAGE, SECTION_COLUMN, CAROUSEL_CARD, MENU, FORM_STEP, CONTAINER].includes(parentType);

      if (parentWidget || group || denyHostpotReparent || (hasHotspotChild && hasTopParentIsHotspot)) {
        return false;
      }

      return true;
    }
  }
}

ButtonWidget.actions = (editor, breakpoint, props) => {
  return (
    <>
      {breakpoint === 'desktop' &&
        <ButtonWidgetDesktopAction editorSession={editor} breakpoint={breakpoint} {...props} />
      }

      {breakpoint === 'mobile' &&
        <ButtonWidgetMobileAction editorSession={editor} breakpoint={breakpoint} {...props} />
      }
    </>
  );
}

const ButtonTextEditor = (props) => {
  const dispatch = useDispatch();

  const [error, setError] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const id = _.get(props, 'widget.uid');
  const value = _.get(props, 'widget.content');

  const handleChange = (e) => {
    var value = e.target.value;

    if (!value || value.trim().length === 0) {
      setError("Please enter button text")
    } else {
      setError(null);
    }

    dispatch(updateWidgetContent(editorSession, id, value));
  }

  return (
    <Property.Dialog
      width={222}
      target={`#${id}`}
      title='Change Button Text'
      onCancel={props.onCancel}
    >
      <Property name='What do you want to call this Button?'>
        <Property.Input value={value}
          onChange={handleChange}
          placeholder="Button Text"
          error={error} />
      </Property>
    </Property.Dialog>
  );
}

// const ButtonTextEditor = ({
//   widgets,
//   onCancel
// }) => {
//   const dispatch = useDispatch();

//   const [error, setError] = useState(null);

//   const editorContext = React.useContext(EditorContext);
//   const editorSession = editorContext.editorSession.id;

//   const widget = widgets?.[0];

//   const id = _.get(widget, 'uid');
//   const value = _.get(widget, 'content');

//   const handleChange = (e) => {
//     var value = e.target.value;

//     if (!value || value.trim().length === 0) {
//       setError("Please enter button text")
//     } else {
//       setError(null);
//     }

//     widgets.forEach((widget) => {
//       dispatch(updateWidgetContent(editorSession, widget.uid, value));
//     });
//   }

//   return (
//     <Property.Dialog
//       width={222}
//       target={`#${id}`}
//       title='Change Button Text'
//       onCancel={onCancel}
//     >
//       <Property name='What do you want to call this Button?'>
//         <Property.Input value={value}
//           onChange={handleChange}
//           placeholder="Button Text"
//           error={error} />
//       </Property>
//     </Property.Dialog>
//   );
// }

const ButtonStyleEditor = ({ widgets, position, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const widget = widgets[0];
  const id = _.get(widget, 'uid', '');

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));

  const [defaultWidgetStyle, setDefaultWidgetStyle] = useState({
    regular: {
      font: {
        style: {
          bold: false,
          italic: false,
          underline: false
        },
        fontSize: "14",
        fontFamily: null,
        lineSpacing: null,
        letterSpacing: null,
        color: null
      },
      background: null,
      border: null,
      shadow: {
        show: false,
        axes: null,
        color: null
      }
    },
    hover: {
      font: {
        style: {
          bold: false,
          italic: false,
          underline: false
        },
        fontSize: "14",
        fontFamily: null,
        lineSpacing: null,
        letterSpacing: null,
        color: null
      },
      background: null,
      border: null,
      shadow: {
        show: false,
        axes: null,
        color: null
      }
    },
    alignment: {
      position: "center",
      margin: 5
    }
  });

  const properties = widgets.length > 1 ? defaultWidgetStyle : _.get(widget, `properties.${breakpoint}`, {});

  const alignment = _.get(properties, 'alignment', {
    position: 'center',
    margin: 5
  });

  const [state, setState] = useState({
    activeTab: 'regular'
  })

  const regularShadowProps = _.get(properties, `regular.shadow`, {});
  const hoverShadowProps = _.get(properties, `hover.shadow`, {});

  const handleTabChange = (key) => {
    setState(() => {
      return {
        ...state,
        activeTab: key
      }
    })
  }

  const getDifferences = (defaultObj, updatedObj) => {
    const result = {};
  
    Object.keys(updatedObj).forEach((key) => {
      const defaultVal = defaultObj[key];
      const updatedVal = updatedObj[key];
  
      if (_.isObject(updatedVal) && !Array.isArray(updatedVal) && updatedVal !== null) {
        const deepDiff = getDifferences(defaultVal || {}, updatedVal);
        if (Object.keys(deepDiff).length > 0) {
          result[key] = deepDiff;
        }
      } else if (!_.isEqual(defaultVal, updatedVal)) {
        result[key] = updatedVal;
      }
    });
  
    return result;
  }

  const handlePropertyChange = (value) => {
    const widgetIds = widgets.map((w) => w.uid);

    if(_.isEqual(value, properties)) return;
    const differences = getDifferences(properties,value);

    if(differences.regular?.border?.color === null || differences?.hover?.border?.color === null) return;

    setDefaultWidgetStyle(value);
    dispatch(updateBulkWidgetProperty(editorSession, widgetIds, breakpoint, differences));
  }

  const handleAlignmentChange = (value) => {
    const position = _.get(value, 'position');

    if (position === 'center') {
      value = {
        ...value,
        margin: 5
      }
    }

    var request = {
      ...properties,
      alignment: {
        ...alignment,
        ...value
      }
    }

    handlePropertyChange(request);
  }

  const handleShadowChange =(field, value)=> {
   var request = {
      ...properties,
      [state.activeTab]: {
        ...properties[state.activeTab],
        shadow: {
          ...properties[state.activeTab].shadow,
          [field]: value
        }
      }
    }

    handlePropertyChange(request);
  }

  return (
    <Property.Dialog className={styles.fontDialog}
      width={246}
      target={`#${id}`}
      title={'Customize Button'}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <Property name="Text Alignment" className={styles.buttonAlignmentProperty}>
          <ButtonAlignment
            value={alignment}
            onChange={handleAlignmentChange}
          />
        </Property>
        <Property name="Button Design" className={styles.buttonDesignProperty}>
          <Tabs activeKey={state.activeTab}
            onSelect={handleTabChange}
            className={styles.buttonStyleTabs}>
            <Tab eventKey="regular" title="Regular" className={styles.buttonStyleTab}>
              <FontEditor
                editorSession={editorSession}
                id={id}
                properties={properties}
                active={'regular'}
                handlePropertyChange={handlePropertyChange}
              />
              <DropShadow id={id} properties={regularShadowProps} handleShadowChange={handleShadowChange} />
            </Tab>
            <Tab eventKey="hover" title="Hover" className={styles.buttonStyleTab}>
              <FontEditor
                editorSession={editorSession}
                id={id}
                properties={properties}
                active={'hover'}
                handlePropertyChange={handlePropertyChange}
              />
              <DropShadow id={id} properties={hoverShadowProps} handleShadowChange={handleShadowChange} />
            </Tab>
          </Tabs>
        </Property>
        
      </Property.Body>
    </Property.Dialog>
  );
}

const FontEditor = ({ editorSession, id, properties, active, handlePropertyChange }) => {
  const dispatch = useDispatch();

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const deviceProperties = _.get(properties, `${active}`, {});
  const border = _.get(deviceProperties, 'border', {});
  const borderWidth = _.get(border, 'width', 0);
  const borderRadius = _.get(border, 'radius', {
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0
  });

  const fontFamily = _.get(deviceProperties, 'font.fontFamily') || 'Montserrat,sans-serif';
  const fontSize = _.get(deviceProperties, 'font.fontSize') || 16;
  const lineSpacing = _.get(deviceProperties, 'font.lineSpacing') || 1.2;
  const letterSpacing = _.get(deviceProperties, 'font.letterSpacing') || 0;
  const fontStyle = _.get(deviceProperties, 'font.style', {});

// To Do Fix
  const [borderColor, setBorderColor] = useState(_.get(border, 'color', null));
  const [textColor,setTextColor] = useState(_.get(deviceProperties, 'font.color', null));
  const [backgroundColor,setBackgroundColor] = useState(_.get(deviceProperties, 'background', null));

  const handleBorderColor = (value) => {
    setBorderColor(value);
  };
  const handleTextColor = (value) => {
    setTextColor(value);
  };
  const handleBackgroundColor = (value) => {
    setBackgroundColor(value);
  };

  useEffect(() => {
    if(!_.isEqual(borderColor, _.get(border, `color`))){
      handleBorderColorChange(borderColor);
      return
    }
    if(!_.isEqual(textColor, _.get(deviceProperties, 'font.color'))){
      handleTextColorChange(textColor)
      return
    }
    if(!_.isEqual(backgroundColor, _.get(deviceProperties, 'background'))){
      handleBackgroundColorChange(backgroundColor)
      return
    }

  }, [borderColor, backgroundColor, textColor]);

  const handleTextColorChange = React.useCallback((color) => {
    var request = {
      ...properties,
      [active]: {
        ...properties[active],
        font: {
          ...deviceProperties.font,
          color: color
        }
      }
    }

    handlePropertyChange(request);
  }, [properties]);

  const handleBackgroundColorChange = React.useCallback((color) => {
    var request = {
      ...properties,
      [active]: {
        ...properties[active],
        background: color
      }
    }

    handlePropertyChange(request);
  }, [properties]);

  const handleBorderColorChange = (color) => {
    var request = {
      ...properties,
      [active]: {
        ...deviceProperties,
        border: {
          ...border,
          color: color
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderWidthChange = (value) => {
    var request = {
      ...properties,
      [active]: {
        ...deviceProperties,
        border: {
          ...border,
          width: value
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderRadiusChange = (radius) => {
    var request = {
      ...properties,
      [active]: {
        ...deviceProperties,
        border: {
          ...border,
          radius: radius
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleFontFamily = (value) => {
    var font = _.get(value, 'font.id');
    var family = _.get(value, 'font.fontFamily');
    if (!family) {
      return;
    }

    var request = {
      ...properties,
      [active]: {
        ...deviceProperties,
        font: {
          ...deviceProperties.font,
          fontFamily: family
        }
      }
    }

    handlePropertyChange(request);
    if (font) {
      dispatch(associateFontsWithWigdet(editorSession, id, [font]));
    }
  }

  const handleSizeChange = (value) => {
    var request = {
      ...properties,
      [active]: {
        ...deviceProperties,
        font: {
          ...deviceProperties.font,
          fontSize: value
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleLineSpacingChange = (value) => {
    var request = {
      ...properties,
      [active]: {
        ...deviceProperties,
        font: {
          ...deviceProperties.font,
          lineSpacing: value
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleLetterSpacingChange = (value) => {
    var request = {
      ...properties,
      [active]: {
        ...deviceProperties,
        font: {
          ...deviceProperties.font,
          letterSpacing: value
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleFontStyleChange = (style) => {
    var request = {
      ...properties,
      [active]: {
        ...deviceProperties,
        font: {
          ...deviceProperties.font,
          style: {
            ...fontStyle,
            [style]: !fontStyle[style]
          }
        }
      }
    }

    handlePropertyChange(request);
  }

  return (
    <>
      <Property.Accordion title={'Color'} className={styles.colorProperty}>
        <Property.Color title={'Text'} value={_.get(deviceProperties, 'font.color', null)} onChange={handleTextColorChange} />
        <Property.Color title={'Background'} gradient={true} clearable={true} value={_.get(deviceProperties, 'background', null)} onChange={handleBackgroundColorChange} />
      </Property.Accordion>

      <Property.Accordion title={'Border'} className={styles.borderProperty}>
        <Property.Color title={'Color'} clearable={true} value={borderColor} onChange={handleBorderColor} />
        {active === 'regular' &&
          <>
            <Property name="Stroke Width" className={styles.buttonStyleProperty}>
              <Property.Slider
                min={0}
                max={10}
                step={1}
                input
                value={borderWidth}
                onChange={handleBorderWidthChange}
              />
            </Property>
            <Property name="Corner Radius" className={styles.buttonStyleProperty}>
              <Property.BorderRadius
                value={borderRadius}
                onChange={handleBorderRadiusChange}
              />
            </Property>
          </>
        }

      </Property.Accordion>
      {active === 'regular' &&
        <Property.Accordion title={'Text'} className={styles.textProperty}>
          <Font.Family
            value={fontFamily || 'Montserrat,sans-serif'}
            onChange={handleFontFamily}
            className={styles.buttonStyleProperty}
          />
          <Font.Size
            value={fontSize}
            onChange={handleSizeChange}
            className={styles.buttonStyleProperty}
            breakpoint={breakpoint}
          />

          <Property className={styles.buttonStyleProperty}>
            <div className={styles.fontActions}>
              <Property.Action className={cx(styles.iconButton, { [styles.active]: _.get(fontStyle, 'bold', false) })}
                onClick={handleFontStyleChange.bind(this, 'bold')}
                tooltip="Bold"
              >
                <i className={'cleverstory-bold'} />
              </Property.Action>
              <Property.Action className={cx(styles.iconButton, { [styles.active]: _.get(fontStyle, 'italic', false) })}
                onClick={handleFontStyleChange.bind(this, 'italic')}
                tooltip="Italic"
              >
                <i className={'cleverstory-italics'} />
              </Property.Action>
              <Property.Action className={cx(styles.iconButton, { [styles.active]: _.get(fontStyle, 'underline', false) })}
                onClick={handleFontStyleChange.bind(this, 'underline')}
                tooltip="Underline"
              >
                <i className={'cleverstory-underline'} />
              </Property.Action>
            </div>
          </Property>
          <Property name="Line Spacing" className={styles.lineSpacingProperty}>
            <Property.Slider
              input
              min={0.5}
              max={3.0}
              step={0.1}
              value={lineSpacing}
              onChange={handleLineSpacingChange}
            />
          </Property>
          <Property name="Character Spacing" className={styles.characterSpacingProperty}>
            <Property.Slider
              input
              min={-0.1}
              max={0.7}
              step={0.05}
              value={letterSpacing}
              onChange={handleLetterSpacingChange}>
            </Property.Slider>
          </Property>
        </Property.Accordion>
      }
    </>
  )
}

const ButtonAlignment = ({ value, onChange }) => {
  const position = _.get(value, 'position') || 'center';
  const margin = _.get(value, 'margin', 0);

  const handleAlignmentChange = (position) => {
    var request = {
      position: position
    }

    onChange(request);
  }

  const handleMarginChange = (margin) => {
    var request = {
      margin: margin
    }

    onChange(request);
  }

  return (
    <div className={styles.buttonAlignment}>
      <div className={styles.options}>
        <Tooltip placement="top" content="Left Align">
          <button className={cx(styles.alignment,
            {
              [styles.active]: position === 'left'
            })}
            onClick={handleAlignmentChange.bind(this, 'left')}
          >
            <i className={cx('cleverstory-align-left')}></i>
          </button>
        </Tooltip>
        <Tooltip placement="top" content="Center Align">
          <button className={cx(styles.alignment,
            {
              [styles.active]: position === 'center'
            })}
            onClick={handleAlignmentChange.bind(this, 'center')}
          >
            <i className={cx('cleverstory-align-center')}></i>
          </button>
        </Tooltip>
        <Tooltip placement="top" content="Right Align">
          <button className={cx(styles.alignment,
            {
              [styles.active]: position === 'right'
            })}
            onClick={handleAlignmentChange.bind(this, 'right')}
          >
            <i className={cx('cleverstory-align-right')}></i>
          </button>
        </Tooltip>
      </div>

      {position !== 'center' &&
        <Property name="Margin" className={styles.marginProperty}>
          <Property.Slider
            min={0}
            max={100}
            step={1}
            value={margin}
            onChange={handleMarginChange}
          />
        </Property>
      }
    </div>
  )
}

export const ButtonMobileStyleEditor = ({ widgets, position, ...rest }) => {
  const dispatch = useDispatch();
  const { editorSession } = React.useContext(EditorContext);
  const editorId = editorSession.id;

  const id = widgets[0]?.uid;

  // Helper to extract shared value
  const getSharedValue = (path, fallback) => {
    const values = widgets.map(widget => _.get(widget, `properties.mobile.${path}`));
    return values.every(val => _.isEqual(val, values[0])) ? values[0] : fallback;
  };
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));

  const alignment = getSharedValue('alignment', {
    position: 'center',
    margin: 5
  });

  const fontSize = getSharedValue('regular.font.fontSize', 14);
  const lineSpacing = getSharedValue('regular.font.lineSpacing', 1.2);
  const letterSpacing = getSharedValue('regular.font.letterSpacing', 0);

  const handlePropertyChange = (value) => {
    const widgetIds = widgets.map(w => w.uid);
    dispatch(updateBulkWidgetProperty(editorId, widgetIds, 'mobile', value));
  };

  const handleAlignmentChange = (value) => {
    const newValue = {
      ...alignment,
      ...value
    };

    if (newValue.position === 'center') {
      newValue.margin = 5;
    }

    handlePropertyChange({ alignment: newValue });
  };

  const handleFontChange = (key, val) => {
    const update = {
      regular: {
        font: {
          [key]: val
        }
      }
    };
    handlePropertyChange(update);
  };

  if(widgets?.length === 0) return;

  return (
    <Property.Dialog
      className={styles.fontDialog}
      width={246}
      target={`#${id}`}
      title={'Customize Button'}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <Property name="Text Alignment" className={styles.buttonAlignmentProperty}>
          <ButtonAlignment
            value={alignment}
            onChange={handleAlignmentChange}
          />
        </Property>

        <Font.Size
          name="Font Size"
          value={fontSize}
          onChange={(val) => handleFontChange('fontSize', val)}
          breakpoint={breakpoint}
        />

        <Property name="Line Spacing" className={styles.lineSpacingProperty}>
          <Property.Slider
            input
            min={0.5}
            max={3.0}
            step={0.1}
            value={lineSpacing}
            onChange={(val) => handleFontChange('lineSpacing', val)}
          />
        </Property>

        <Property name="Character Spacing" className={styles.characterSpacingProperty}>
          <Property.Slider
            input
            min={-0.1}
            max={0.7}
            step={0.05}
            value={letterSpacing}
            onChange={(val) => handleFontChange('letterSpacing', val)}
          />
        </Property>
      </Property.Body>
    </Property.Dialog>
  );
};

/**
 * Generate Styles
 * 
*/
export function getStyles(properties, breakpoint, active) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const defaultProperties = _.get(properties, `desktop`);
  const deviceProperties = _.get(properties, `${breakpoint}`);
  const defaultStyles = _.get(defaultProperties, `regular`, {});

  //font-color
  const colorRgb = _.get(defaultProperties, `${active}.font.color`, null);
  const color = getColorStyles(colorRgb);
  styles = {
    ...styles,
    ...color
  }

  //background
  const backgroundRgb = _.get(defaultProperties, `${active}.background`, null);
  var background = 'inherit';
  if (backgroundRgb) {
    background = getColorsConfig(backgroundRgb);
  }

  styles['background'] = background;

  const font = _.get(defaultStyles, 'font', {});

  //font-family
  const fontFamily = _.get(font, 'fontFamily');
  if (fontFamily) {
    styles = {
      ...styles,
      fontFamily: fontFamily
    }
  }

  //font-size
  const fontSizeString = _.get(deviceProperties, 'regular.font.fontSize', null);
  var fontSize = 'inherit'
  if (fontSizeString) {
    fontSize = parseInt(fontSizeString) + 'px';
  }

  styles['fontSize'] = fontSize;

  //line spacing
  const lineSpacingString = _.get(deviceProperties, 'regular.font.lineSpacing', null);
  if (lineSpacingString) {
    const lineSpacing = parseFloat(lineSpacingString) + 'em';
    styles['lineHeight'] = lineSpacing;
  }

  //Letter Spacing
  const letterSpacingString = _.get(deviceProperties, 'regular.font.letterSpacing', null);
  if (letterSpacingString) {
    const letterSpacing = parseFloat(letterSpacingString) + 'em';
    styles['letterSpacing'] = letterSpacing;
  }

  //font-style
  const bold = _.get(font, 'style.bold', false);
  if (bold) {
    styles['fontWeight'] = 'bold';
  }

  const italic = _.get(font, 'style.italic', false);
  if (italic) {
    styles['fontStyle'] = 'italic';
  }

  const underline = _.get(font, 'style.underline', false);
  if (underline) {
    styles['textDecoration'] = 'underline';
  }

  //border
  const border = _.get(defaultStyles, 'border', {});
  const borderStyle = getBorderStyles(border);
  styles = {
    ...styles,
    ...borderStyle
  }

  //DropShadow
  const shadow = _.get(defaultProperties, `${active}.shadow`, null);
  const shadowStyle = getBoxShadow(shadow);
  styles = {
    ...styles,
    ...shadowStyle
  }

  //alignment
  const alignment = _.get(deviceProperties, 'alignment', {});

  var position = _.get(alignment, 'position', 'center');
  styles['justifyContent'] = position;
  styles['textAlign'] = position;

  var margin = _.get(alignment, 'margin', 0);
  if (position === 'left') {
    styles = {
      ...styles,
      paddingLeft: `${margin}px`
    }
  } else if (position === 'right') {
    styles = {
      ...styles,
      paddingRight: `${margin}px`
    }
  }

  return styles;
}

export function getColorStyles(value) {
  if (!value) {
    return null;
  }

  const colorRgb = value;

  var color = 'inherit';
  if (colorRgb) {
    color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
  }

  var styles = {
    color: color
  };

  return styles;
}

export function getBorderStyles(value) {
  if (!value) {
    return null;
  }

  var styles = {};
  const topLeft = _.get(value, 'radius.topLeft', null);
  const topRight = _.get(value, 'radius.topRight', null);
  const bottomLeft = _.get(value, 'radius.bottomLeft', null);
  const bottomRight = _.get(value, 'radius.bottomRight', null);

  if (topLeft) {
    styles['borderTopLeftRadius'] = parseInt(topLeft) + 'px';
  }

  if (topRight) {
    styles['borderTopRightRadius'] = parseInt(topRight) + 'px';
  }

  if (bottomLeft) {
    styles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px';
  }

  if (bottomRight) {
    styles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px';
  }

  const colorRgb = _.get(value, 'color.color') || _.get(value, 'color');

  var color = 'transparent';
  if (colorRgb) {
    color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
  }

  styles['borderColor'] = color;

  const borderWidthString = _.get(value, 'width', null);
  var borderWidget = parseInt(borderWidthString) + 'px';

  styles['borderWidth'] = borderWidget;

  return styles;
}


ButtonWidget.processRequest = (widget) => {
  const actions = _.get(widget, 'actions');
  
  const updated = (actions || []).map((action)=> {
    var value = _.get(action, 'value.content.id') || _.get(action, 'value', null);

    if(value != null) {
      if(action.type == 'asset') {
        return {
          ...action,
          value: value
        }
      }
      
      return action;
    }
  })

  return {
    ...widget,
    actions: updated
  }
}

export { ButtonWidget, ButtonWidgetMobileAction, ButtonTextEditor, ButtonStyleEditor };

